import React, { useMemo } from 'react';
import { Box, TextField, Typography, InputAdornment, IconButton, Link, Alert, Fade, Button } from '@mui/material';
import { Email, Lock, Phone, Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import type { AuthLogicReturn, Config } from '../../shared/types';
import { maskPhoneNumber } from '../../shared/utils';
import { useThemeMode } from '../../../../contexts/ThemeProvider';

interface Props {
  authLogic: AuthLogicReturn;
  config: Config;
}

const ModernFormFields: React.FC<Props> = ({ authLogic, config }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const {
    loginMode,
    formData,
    errors,
    showPassword,
    otpSent,
    resetEmailSent,
    updateFormData,
    togglePasswordVisibility,
    goToForgotPassword,
    backToLogin,
    changeLoginMode,
    LOGIN_MODES
  } = authLogic;

  const loginMethodsConfig = config?.login?.loginMethods || {};
  const primaryColor = config?.theme?.primary;

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: isDark 
        ? 'rgba(255, 255, 255, 0.08)' 
        : 'rgba(255, 255, 255, 0.9)',
      border: 'none',
      minHeight: { xs: '52px', md: '56px' },
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: isDark 
          ? 'rgba(255, 255, 255, 0.12)' 
          : 'rgba(255, 255, 255, 0.95)'
      },
      '&.Mui-focused': {
        backgroundColor: isDark 
          ? 'rgba(255, 255, 255, 0.15)' 
          : '#fff',
        boxShadow: `0 0 0 2px ${primaryColor}40`
      },
      '& fieldset': { border: 'none' },
      '& input': {
        color: isDark ? '#fff' : '#333',
        fontSize: { xs: '16px', md: '14px' },
        '&::placeholder': {
          color: isDark ? 'rgba(255,255,255,0.6)' : '#999',
          opacity: 1
        }
      }
    },
    '& .MuiFormHelperText-root': {
      color: '#ffcdd2',
      fontSize: '0.75rem'
    }
  };

  return (
    <>
      <Box
        sx={{
          minHeight: loginMode === LOGIN_MODES.EMAIL ? '250px' : loginMode === LOGIN_MODES.PHONE && !otpSent ? '110px' : '180px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={loginMode}
            initial={{ opacity: 0, x: 100, rotateY: 15 }}
            animate={{ opacity: 1, x: 0, rotateY: 0 }}
            exit={{ opacity: 0, x: -100, rotateY: -15 }}
            transition={{
              duration: 0.4,
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
            style={{ position: 'absolute', width: '100%' }}
          >
            {loginMode === LOGIN_MODES.EMAIL && (
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.9)', 
                    mb: 1, 
                    fontWeight: 500 
                  }}
                >
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  sx={{ ...textFieldStyles, mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: isDark ? '#aaa' : '#666', fontSize: 20 }} />
                      </InputAdornment>
                    )
                  }}
                />

                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.9)', 
                    mb: 1, 
                    fontWeight: 500 
                  }}
                >
                  Password
                </Typography>
                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••••"
                  value={formData.password}
                  onChange={(e) => updateFormData('password', e.target.value)}
                  error={!!errors.password}
                  helperText={errors.password}
                  sx={{ ...textFieldStyles, mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: isDark ? '#aaa' : '#666', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                          sx={{
                            color: isDark ? '#aaa' : '#666',
                            minWidth: { xs: '44px', md: '48px' },
                            minHeight: { xs: '44px', md: '48px' }
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                {loginMethodsConfig.forgotPassword?.enabled !== false && (
                  <Box display="flex" justifyContent="flex-end" mb={1}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={goToForgotPassword}
                      sx={{
                        color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.9)',
                        textDecoration: 'underline',
                        textDecorationColor: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.5)',
                        fontWeight: 500,
                        minHeight: { xs: '44px', md: 'auto' },
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover': {
                          color: 'white',
                          textDecorationColor: 'white'
                        }
                      }}
                    >
                      Forgot password?
                    </Link>
                  </Box>
                )}
              </Box>
            )}

            {loginMode === LOGIN_MODES.PHONE && loginMethodsConfig.emailWithOtp?.enabled && (
              <Box>
                {!otpSent ? (
                  <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.9)', 
                        mb: 1, 
                        fontWeight: 500 
                      }}
                    >
                      Email
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="+1 (555) 000-0000"
                      value={formData.emailWithOtp}
                      onChange={(e) => updateFormData('emailWithOtp', e.target.value)}
                      error={!!errors.emailWithOtp}
                      helperText={errors.emailWithOtp}
                      sx={{ ...textFieldStyles, mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone sx={{ color: isDark ? '#aaa' : '#666', fontSize: 20 }} />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Box>
                ) : (
                  <Fade in={otpSent}>
                    <Box>
                      <Alert
                        severity="success"
                        sx={{
                          mb: 2,
                          borderRadius: 2,
                          backgroundColor: isDark 
                            ? 'rgba(76, 175, 80, 0.15)' 
                            : 'rgba(76, 175, 80, 0.2)',
                          color: 'white',
                          border: `1px solid ${isDark ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.4)'}`,
                          '& .MuiAlert-icon': { color: 'white' }
                        }}
                      >
                        OTP sent to {maskPhoneNumber(formData.emailWithOtp)}
                      </Alert>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.9)', 
                          mb: 1, 
                          fontWeight: 500 
                        }}
                      >
                        Enter OTP
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter 6-digit OTP"
                        value={formData.otp}
                        onChange={(e) => updateFormData('otp', e.target.value)}
                        error={!!errors.otp}
                        helperText={errors.otp}
                        inputProps={{ maxLength: 6 }}
                        sx={{
                          ...textFieldStyles,
                          mb: 2,
                          '& .MuiOutlinedInput-root': {
                            ...textFieldStyles['& .MuiOutlinedInput-root'],
                            '& input': {
                              ...textFieldStyles['& .MuiOutlinedInput-root']['& input'],
                              textAlign: 'center',
                              fontSize: { xs: '16px', md: '1.1rem' },
                              letterSpacing: '0.3rem'
                            }
                          }
                        }}
                      />
                    </Box>
                  </Fade>
                )}
              </Box>
            )}

            {loginMode === LOGIN_MODES.FORGOT_PASSWORD && (
              <Box>
                {!resetEmailSent ? (
                  <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.9)', 
                        mb: 1, 
                        fontWeight: 500 
                      }}
                    >
                      Email Address
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter your email"
                      value={formData.resetEmail}
                      onChange={(e) => updateFormData('resetEmail', e.target.value)}
                      error={!!errors.resetEmail}
                      helperText={errors.resetEmail}
                      sx={{ ...textFieldStyles, mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email sx={{ color: isDark ? '#aaa' : '#666', fontSize: 20 }} />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Box>
                ) : (
                  <Fade in={resetEmailSent}>
                    <Box>
                      <Alert
                        severity="success"
                        sx={{
                          mb: 2,
                          borderRadius: 2,
                          backgroundColor: isDark 
                            ? 'rgba(76, 175, 80, 0.15)' 
                            : 'rgba(76, 175, 80, 0.2)',
                          color: 'white',
                          border: `1px solid ${isDark ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.4)'}`,
                          '& .MuiAlert-icon': { color: 'white' }
                        }}
                      >
                        Reset instructions sent to {formData.resetEmail}
                      </Alert>
                    </Box>
                  </Fade>
                )}

                <Button
                  startIcon={<ArrowBack />}
                  onClick={backToLogin}
                  sx={{
                    mb: 5,
                    textTransform: 'none',
                    color: isDark ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.8)',
                    fontWeight: 500,
                    minHeight: { xs: '44px', md: '40px' },
                    '&:hover': {
                      color: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Back to sign in
                </Button>
              </Box>
            )}
          </motion.div>
        </AnimatePresence>
      </Box>
    </>
  );
};

export default ModernFormFields;